import React from "react";
import { api } from "../lib/api";

/** Trimite eroarea către backend (best-effort, fără await, nu blochează niciodată). */
export function reportErrorToBackend({ message, stack, context, extra }) {
  try {
    const payload = {
      message: String(message || "").slice(0, 1000),
      stack: String(stack || "").slice(0, 5000),
      url: (typeof window !== "undefined" && window.location?.href) || "",
      user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
      context: String(context || ""),
      extra: extra || null,
    };
    // Fire-and-forget — orice eroare aici e ignorată în mod intenționat.
    api.post("/frontend-errors", payload).catch(() => {});
  } catch { /* noop */ }
}

/** Listener global pentru erori necapturate (handlers, promisiuni). Apel din index.js sau App.js o singură dată. */
export function installGlobalErrorReporting() {
  if (typeof window === "undefined") return;
  if (window.__convic_err_reporting_installed) return;
  window.__convic_err_reporting_installed = true;
  window.addEventListener("error", (e) => {
    reportErrorToBackend({
      message: e?.message || "window.error",
      stack: e?.error?.stack || `${e?.filename || ""}:${e?.lineno || ""}:${e?.colno || ""}`,
      context: "window.error",
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    reportErrorToBackend({
      message: (reason && (reason.message || String(reason))) || "unhandledrejection",
      stack: (reason && reason.stack) || "",
      context: "unhandledrejection",
    });
  });
}

/**
 * Global error boundary. Prevents the entire app from going blank
 * when any child throws during render. Shows a user-friendly fallback
 * with a "Reîncarcă pagina" button instead.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for debugging — visible in browser console + any external monitor.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Captured error:", error, info);
    // Trimite eroarea la backend pentru investigare (best-effort, fără await).
    try {
      reportErrorToBackend({
        message: error?.message || "(no message)",
        stack: error?.stack || "",
        context: "ErrorBoundary",
        extra: { componentStack: info?.componentStack || null },
      });
    } catch { /* noop */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleCopy = async () => {
    const url = (() => { try { return window.location.href; } catch { return "?"; } })();
    const ua = (() => { try { return navigator.userAgent; } catch { return "?"; } })();
    const ts = new Date().toISOString();
    const stack = this.state.error?.stack || "(fără stack)";
    const msg = this.state.error?.message || "(fără mesaj)";
    const text = `[ConVic ERP] Eroare frontend\nData: ${ts}\nURL: ${url}\nUser-Agent: ${ua}\n\nMesaj: ${msg}\n\nStack:\n${stack}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2500);
        return;
      }
    } catch { /* fallback below */ }
    // Fallback: textarea + execCommand (funcționează pe iOS Safari mai vechi)
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "A apărut o eroare neașteptată.";
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center p-6 bg-zinc-950 text-white"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full border border-red-700 bg-zinc-900 rounded-lg p-6 shadow-xl text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h1 className="text-xl font-bold mb-2">Hopa, ceva nu a mers</h1>
          <p className="text-sm text-zinc-300 mb-4">
            Aplicația a întâmpinat o eroare neașteptată. Încearcă să reîncarci pagina.
          </p>
          <pre className="text-[10px] text-zinc-400 bg-black/40 rounded p-2 mb-4 max-h-24 overflow-auto text-left whitespace-pre-wrap">
            {msg}
          </pre>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleCopy}
              className={`px-4 py-2 rounded text-sm font-bold transition-colors ${this.state.copied ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
              data-testid="error-boundary-copy"
            >
              {this.state.copied ? "✓ Copiat — trimite-mi pe chat" : "📋 Copiază eroarea"}
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-semibold"
              data-testid="error-boundary-dismiss"
            >
              Închide eroarea
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold"
              data-testid="error-boundary-reload"
            >
              🔄 Reîncarcă pagina
            </button>
          </div>
        </div>
      </div>
    );
  }
}
